import { Descendant, Text } from 'slate'

import { IPage } from '@makyoapp/makyo-sdk/models/AppBuilder'
import {
  IActionType,
  VariableResultColumn,
  VariableResultColumnData,
  VariableResultCommon,
  VariableResultCurrentListItem,
  VariableResultGlobalVariable,
  VariableResultProfileData,
  VariableResultSingleCell,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'

import { DEFAULT_LANGUAGE, DEFAULT_USER_SPECIFIC, IDB } from '@/core/db'
import { FilterConfig } from '@/core/db/operations/filter'
import cookies from '@/lib/cookies'
import { generateRandomId } from '@/lib/random-id'
import { isDescendant, parseStringToDescendant } from '@/lib/slate'

import { IAppLayer } from '../app/layer'
import { IAppPage } from '../app/page'
import { globalVariableClient } from '../global-variables'
import { parseProfileData } from './profile-data'
import { parseSingleCellValue } from './single-cell'
import { parseVariableSelect } from './variable-select'

export function parseVariableText(
  node: Descendant | Descendant[],
  dbClient: IDB | undefined,
  container?: IAppLayer,
  userEmail?: string,
  lang = DEFAULT_LANGUAGE,
  user = DEFAULT_USER_SPECIFIC,
  tableId?: string,
  rowId?: string,
  outputCache?: Map<string, any>,
  external?: Record<string, any>
) {
  if (!container || !dbClient) return ''

  if (Text.isText(node)) {
    return node.text
  }

  let result = ''

  if (Array.isArray(node)) {
    result += node
      .map((n) =>
        parseVariableText(n, dbClient, container, userEmail, lang, user, tableId, rowId, outputCache, external)
      )
      .join('')
  } else {
    const children = node.children
      ? node.children
          .map((n) =>
            parseVariableText(n, dbClient, container, userEmail, lang, user, tableId, rowId, outputCache, external)
          )
          .join('')
      : ''

    switch (Number(node.type)) {
      case VariableTextFeatureFlag.PageTitle: {
        const pageTitleValue = parsePageTitle(container, lang, user, rowId)
        if (!pageTitleValue) break
        result += pageTitleValue
        break
      }

      case VariableTextFeatureFlag.PageLabel: {
        const pageLabelValue = parsePageContent(container as IAppPage, lang, user, '_Label', rowId)
        if (!pageLabelValue) break
        result += pageLabelValue
        break
      }

      case VariableTextFeatureFlag.PageSubtitle: {
        const pageSubtitleValue = parsePageContent(container as IAppPage, lang, user, '_Subtitle', rowId)
        if (!pageSubtitleValue) break
        result += pageSubtitleValue
        break
      }

      case VariableTextFeatureFlag.PageDescription: {
        const pageDescriptionValue = parsePageContent(container as IAppPage, lang, user, '_Description', rowId)
        if (!pageDescriptionValue) break
        result += pageDescriptionValue
        break
      }

      case VariableTextFeatureFlag.SingleCell: {
        let val = node as unknown as VariableResultSingleCell

        const singleCellValue = parseSingleCellValue(
          {
            ...val,
            row: Number(val.row) - 1,
          },
          dbClient,
          lang,
          user
        )
        if (!singleCellValue) break
        result += singleCellValue
        break
      }

      case VariableTextFeatureFlag.CurrentListItem: {
        let currentListItemNode = node as unknown as VariableResultCurrentListItem

        const table = dbClient.tablesMap.get(tableId!)
        const rowIdx = rowId ? table?.GetRowIndexByRowId(rowId, lang, user) : -1

        const currentListValue = parseSingleCellValue(
          {
            tbl: tableId,
            col: currentListItemNode.col,
            row: String(rowIdx),
            userSpecific: currentListItemNode.userSpecific,
          } as VariableResultSingleCell,
          dbClient,
          lang,
          user
        )
        if (!currentListValue) break
        result += currentListValue
        break
      }

      case VariableTextFeatureFlag.ColumnData: {
        let columnDataNode = node as unknown as VariableResultColumnData

        const table = dbClient.tablesMap.get(columnDataNode.tbl!)
        const rowIdx = rowId ? table?.GetRowIndexByRowId(rowId, lang, user) : -1

        const columnDataValue = parseSingleCellValue(
          {
            tbl: columnDataNode.tbl,
            col: columnDataNode.col,
            row: String(rowIdx),
            userSpecific: columnDataNode.userSpecific,
          } as VariableResultSingleCell,
          dbClient,
          lang,
          user
        )
        if (!columnDataValue) break
        result += columnDataValue
        break
      }

      case VariableTextFeatureFlag.CurrentRow: {
        let currentRowNode = node as unknown as VariableResultColumnData

        const table = dbClient.tablesMap.get(tableId!)
        const rowIdx = rowId ? table?.GetRowIndexByRowId(rowId, lang, user) : -1

        const currentRowValue = parseSingleCellValue(
          {
            tbl: tableId,
            col: currentRowNode.col,
            row: String(rowIdx),
            userSpecific: currentRowNode.userSpecific,
          } as VariableResultSingleCell,
          dbClient,
          lang,
          user
        )
        if (!currentRowValue) break
        result += currentRowValue
        break
      }

      case VariableTextFeatureFlag.Column: {
        let columnNode = node as unknown as VariableResultColumn

        const table = dbClient.tablesMap.get(tableId!)
        const rowIdx = rowId ? table?.GetRowIndexByRowId(rowId, lang, user) : -1

        const columnValue = parseSingleCellValue(
          {
            tbl: tableId,
            col: columnNode.col,
            row: String(rowIdx),
          } as VariableResultSingleCell,
          dbClient,
          lang,
          user
        )
        if (!columnValue) break
        result += columnValue
        break
      }

      case VariableTextFeatureFlag.ProfileData: {
        const profileDataValue = parseProfileData(
          (node as unknown as VariableResultProfileData).col,
          dbClient,
          userEmail
        )
        if (!profileDataValue) break
        result += profileDataValue
        break
      }

      case VariableTextFeatureFlag.SessionID: {
        const sessionIDValue = cookies.getAppSessionId(window.appID)
        if (!sessionIDValue) break
        result += sessionIDValue
        break
      }

      case VariableTextFeatureFlag.UniqueID: {
        const uniqueIDValue = generateRandomId()
        if (!uniqueIDValue) break
        result += uniqueIDValue
        break
      }

      case VariableTextFeatureFlag.NewsletterEmail: {
        result += external?.newsletter?.email || ''
        break
      }

      case VariableTextFeatureFlag.DateTime: {
        const currentDateTime = new Date().toISOString()
        result += currentDateTime
        break
      }

      case VariableTextFeatureFlag.ActionOutput: {
        let actionOutputNode = node as any

        if (actionOutputNode.actionType === IActionType.FormAddRow) {
          const data = outputCache?.get(actionOutputNode.value.actionId)
          if (data) {
            result += data[actionOutputNode.value.colId]?.toString() ?? ''
          }
        }
        break
      }

      case VariableTextFeatureFlag.GlobalVariable: {
        const varName = (node as unknown as VariableResultGlobalVariable).value
        const value = globalVariableClient?.getValue(varName)
        if (!value) break

        result += value
        break
      }

      default: {
        const n = node as unknown as VariableResultCommon
        if (n.value) {
          result += n.value
        }
        break
      }
    }

    result += children
  }

  return result
}

export function parseVariableTextSingleCell(
  node: Descendant | Descendant[],
  dbClient?: IDB,
  lang = DEFAULT_LANGUAGE,
  user = DEFAULT_USER_SPECIFIC,
  userEmail?: string
) {
  if (!dbClient) return ''

  if (Text.isText(node)) {
    return node.text
  }

  let result = ''

  if (Array.isArray(node)) {
    result += node.map((n) => parseVariableTextSingleCell(n, dbClient, lang, user, userEmail)).join('')
  } else {
    const children = node.children
      ? node.children.map((n) => parseVariableTextSingleCell(n, dbClient, lang, user, userEmail)).join('')
      : ''

    switch (Number(node.type)) {
      case VariableTextFeatureFlag.SingleCell: {
        let val = node as unknown as VariableResultSingleCell
        const singleCellValue = parseSingleCellValue(
          {
            ...val,
            row: Number(val.row) - 1,
          },
          dbClient,
          lang,
          user
        )
        if (!singleCellValue) break
        result += singleCellValue
        break
      }

      case VariableTextFeatureFlag.ProfileData: {
        const profileDataValue = parseProfileData(
          (node as unknown as VariableResultProfileData).col,
          dbClient,
          userEmail
        )
        if (!profileDataValue) break
        result += profileDataValue
        break
      }

      case VariableTextFeatureFlag.SessionID: {
        const sessionIDValue = cookies.getAppSessionId(window.appID)
        if (!sessionIDValue) break
        result += sessionIDValue
        break
      }

      case VariableTextFeatureFlag.UniqueID: {
        const uniqueIDValue = generateRandomId()
        if (!uniqueIDValue) break
        result += uniqueIDValue
        break
      }

      case VariableTextFeatureFlag.GlobalVariable: {
        const varName = (node as unknown as VariableResultGlobalVariable).value
        const value = globalVariableClient?.getValue(varName)
        if (!value) break

        result += value
        break
      }

      default: {
        const n = node as unknown as VariableResultCommon
        if (n.value) {
          result += n.value
        }
        break
      }
    }

    result += children
  }

  return result
}

export function parsePageTitle(
  container: IAppLayer,
  lang = DEFAULT_LANGUAGE,
  user = DEFAULT_USER_SPECIFIC,
  rowId?: string
) {
  if (!container) return ''

  const dbClient = container.dbClient

  const isDetailPage = !!container?.settings._TableID

  // handle detail page
  if (isDetailPage) {
    let tableId = container?.settings._TableID
    if (!tableId) return ''

    const table = dbClient.tablesMap.get(tableId)
    if (!table) return ''

    let pageTitleCol = container.settings._Settings._Title

    const isFormAddScreen = pageTitleCol.startsWith('form-add-')
    const isFormDetailScreen = pageTitleCol.startsWith('form-detail-')

    if (isFormAddScreen) {
      return 'Add New Item'
    }
    if (isFormDetailScreen) {
      return 'Update Item'
    }

    if (typeof rowId !== 'undefined') {
      let cell = table.GetCellDataByRowId(pageTitleCol, rowId, lang, user)
      if (!cell) {
        if (lang === DEFAULT_LANGUAGE) return ''
        cell = table.GetCellDataByRowId(pageTitleCol, rowId, DEFAULT_LANGUAGE, user)
      }
      if (!cell) return ''
      return cell.toString()
    }
  }

  // handle normal page
  let pageTitle = container?.settings?._Settings?._Title ?? ''

  if (isDescendant(pageTitle)) {
    const node = parseStringToDescendant(pageTitle)
    pageTitle = parseVariableTextSingleCell(node, dbClient, lang, user)
  }

  return pageTitle
}

export const parsePageContent = (
  container: IAppPage,
  lang = DEFAULT_LANGUAGE,
  user = DEFAULT_USER_SPECIFIC,
  field: string,
  rowId?: string
) => {
  if (!container) return ''

  const dbClient = container.dbClient
  const isDetailPage = !!container?.settings._TableID

  if (isDetailPage) {
    let tableId = container?.settings._TableID
    if (!tableId) return ''

    let pageLabelCol = (container.settings?.[field as keyof IPage] as string) || ''

    const table = dbClient.tablesMap.get(tableId)
    if (!table) return ''

    if (typeof rowId !== 'undefined') {
      let cell = table.GetCellDataByRowId(pageLabelCol, rowId, lang, user)
      if (!cell) {
        if (lang === DEFAULT_LANGUAGE) return ''
        cell = table.GetCellDataByRowId(pageLabelCol, rowId, DEFAULT_LANGUAGE, user)
      }
      if (!cell) return ''
      return cell.toString()
    }
  }

  let pageLabel = (container.settings?.[field as keyof IPage] as string) || ''

  if (isDescendant(pageLabel)) {
    const node = parseStringToDescendant(pageLabel)
    pageLabel = parseVariableTextSingleCell(node, dbClient, lang, user)
  }

  return pageLabel
}

export function parseTemporaryVariable(
  actionId: string,
  allActions: any[],
  dbClient: IDB,
  activeLayerClient?: IAppLayer,
  userEmail?: string,
  language = DEFAULT_LANGUAGE,
  userSpecific = DEFAULT_USER_SPECIFIC,
  tableId?: string,
  rowId?: string,
  outputCache?: Map<string, any>,
  external?: Record<string, any>
) {
  const actionOutput = allActions.find((action: any) => action.id === actionId)
  if (!actionOutput) return null
  return parseVariableText(
    actionOutput.settings.value,
    dbClient,
    activeLayerClient,
    userEmail,
    language,
    userSpecific,
    tableId,
    rowId,
    outputCache,
    external
  )
}

/**
 * Async function to parse action outputs (temporary variables and filter data)
 * This is separate from parseVariableText to keep that function synchronous
 * TODO (ACTION-WORKFLOWS): REFACTOR THIS
 */
export async function parseFilterDataAsync(
  actionId: string,
  allActions: any[],
  dbClient: IDB | undefined,
  activeLayerClient?: IAppLayer,
  userEmail?: string,
  language = DEFAULT_LANGUAGE,
  userSpecific = DEFAULT_USER_SPECIFIC,
  external?: {
    detailsPage?: {
      detailTableId?: string
      detailRowId?: string
      detailRowIndex?: number
    }
  }
) {
  if (!actionId || !allActions || !dbClient) return null

  const actionOutput = allActions.find((action: any) => action.id === actionId)
  if (!actionOutput) return null

  // Handle Filter Data
  if (actionOutput.type === IActionType.FilterData) {
    const chosenTableId = actionOutput.settings.tableId
    const dbTable = dbClient?.tablesMap.get(chosenTableId)
    if (!dbTable) return null

    let detailRowId = external?.detailsPage?.detailRowId
    const detailRowIndex = external?.detailsPage?.detailRowIndex

    if (!detailRowId && !!detailRowIndex && Number(detailRowIndex) !== -1) {
      detailRowId = dbTable.GetDbWorkerRows(language, userSpecific)[detailRowIndex]._id
    }

    const isReturnFirstOnly = actionOutput.settings.returnFirstOnly
    const filterConditions = actionOutput.settings.conditions.conditions.map((condition: any) => {
      const hasRhs = condition.rhs
      const hasRhsEnd = condition.rhsEnd
      let rhs: any = null
      let rhsEnd: any = null
      let rhsValue: any = null
      if (hasRhs) {
        // Only RHS are normalized to text because RHS are the only possible value that come from action output (Temporary Data) ATM
        // Todo move this RHS parsing to `filtering.ts`
        const isActionOutput = condition.rhs.actionType === IActionType.TemporaryVariable
        if (isActionOutput) {
          rhsValue = parseTemporaryVariable(
            condition.rhs.actionId ?? '',
            allActions ?? [],
            dbClient,
            activeLayerClient,
            userEmail,
            language,
            userSpecific,
            chosenTableId,
            external?.detailsPage?.detailRowId
          )

          if (hasRhsEnd) {
            rhsEnd = {
              flag: VariableSelectFeatureFlag.ActionOutput,
              value: parseTemporaryVariable(
                condition.rhsEnd.actionId ?? '',
                allActions ?? [],
                dbClient,
                activeLayerClient,
                userEmail,
                language,
                userSpecific,
                chosenTableId,
                detailRowId
              ),
            }
          }
          rhs = {
            flag: VariableSelectFeatureFlag.ActionOutput,
            value: rhsValue,
          }
        } else {
          rhsValue = parseVariableSelect(
            condition.rhs,
            dbClient,
            userEmail,
            language,
            userSpecific,
            chosenTableId,
            detailRowId
          )

          if (hasRhsEnd) {
            rhsEnd = {
              flag: VariableSelectFeatureFlag.ActionOutput,
              value: parseVariableSelect(
                condition.rhsEnd,
                dbClient,
                userEmail,
                language,
                userSpecific,
                chosenTableId,
                detailRowId
              ),
            }
          }
          rhs = {
            flag: VariableSelectFeatureFlag.ActionOutput,
            value: rhsValue,
          }
        }
      }

      return {
        ...condition,
        ...(hasRhs ? { rhs } : {}),
        ...(hasRhsEnd ? { rhsEnd } : {}),
      }
    })
    const filterConditionIsActionOutput = actionOutput.settings.conditions.isAny ?? true

    if (filterConditions) {
      try {
        // Create FilterConfig for the dbTable.GetFormattedData method
        const filterConfig: FilterConfig = {
          settings: {
            conditions: filterConditions,
            isAny: filterConditionIsActionOutput,
          },
          userEmail: userEmail || '',
          tableId: chosenTableId,
          detailTableId: external?.detailsPage?.detailTableId,
          detailRowId: external?.detailsPage?.detailRowId,
        }

        // Apply filter using GetFormattedData
        const filteredResult = await dbTable.GetFormattedData(language, userSpecific, undefined, filterConfig)
        // Return based on returnFirstOnly setting
        if (isReturnFirstOnly) {
          return filteredResult.rows.length > 0 ? filteredResult.rows[0] : null
        } else {
          return filteredResult.rows
        }
      } catch (error) {
        console.error('Error applying filter:', error)
        return null
      }
    }
  }

  return null
}

export async function parseVariableTextActionOutput(
  node: Descendant | Descendant[],
  dbClient: IDB | undefined,
  container?: IAppLayer,
  userEmail?: string,
  lang = DEFAULT_LANGUAGE,
  user = DEFAULT_USER_SPECIFIC,
  tableId?: string,
  rowId?: string,
  allActions?: any[],
  outputCache?: Map<string, any>,
  external?: Record<string, any>
) {
  if (!container || !dbClient) return ''

  if (Text.isText(node)) {
    return node.text
  }

  let result = ''

  if (Array.isArray(node)) {
    const results = await Promise.all(
      node.map((n) =>
        parseVariableTextActionOutput(
          n,
          dbClient,
          container,
          userEmail,
          lang,
          user,
          tableId,
          rowId,
          allActions,
          outputCache,
          external
        )
      )
    )
    result += results.join('')
  } else {
    const children = node.children
      ? await Promise.all(
          node.children.map((n) =>
            parseVariableTextActionOutput(
              n,
              dbClient,
              container,
              userEmail,
              lang,
              user,
              tableId,
              rowId,
              allActions,
              outputCache,
              external
            )
          )
        ).then((results) => results.join(''))
      : ''

    switch (Number(node.type)) {
      case VariableTextFeatureFlag.PageTitle: {
        const pageTitleValue = parsePageTitle(container, lang, user, rowId)
        if (!pageTitleValue) break
        result += pageTitleValue
        break
      }

      case VariableTextFeatureFlag.PageLabel: {
        const pageLabelValue = parsePageContent(container as IAppPage, lang, user, '_Label', rowId)
        if (!pageLabelValue) break
        result += pageLabelValue
        break
      }

      case VariableTextFeatureFlag.PageSubtitle: {
        const pageSubtitleValue = parsePageContent(container as IAppPage, lang, user, '_Subtitle', rowId)
        if (!pageSubtitleValue) break
        result += pageSubtitleValue
        break
      }

      case VariableTextFeatureFlag.PageDescription: {
        const pageDescriptionValue = parsePageContent(container as IAppPage, lang, user, '_Description', rowId)
        if (!pageDescriptionValue) break
        result += pageDescriptionValue
        break
      }

      case VariableTextFeatureFlag.SingleCell: {
        let val = node as unknown as VariableResultSingleCell

        const singleCellValue = parseSingleCellValue(
          {
            ...val,
            row: Number(val.row) - 1,
          },
          dbClient,
          lang,
          user
        )
        if (!singleCellValue) break
        result += singleCellValue
        break
      }

      case VariableTextFeatureFlag.CurrentListItem: {
        let currentListItemNode = node as unknown as VariableResultCurrentListItem

        const table = dbClient.tablesMap.get(tableId!)
        const rowIdx = rowId ? table?.GetRowIndexByRowId(rowId, lang, user) : -1

        const currentListValue = parseSingleCellValue(
          {
            tbl: tableId,
            row: String(rowIdx),
            col: currentListItemNode.col,
            userSpecific: currentListItemNode.userSpecific,
          } as VariableResultSingleCell,
          dbClient,
          lang,
          user
        )
        if (!currentListValue) break
        result += currentListValue
        break
      }

      case VariableTextFeatureFlag.ColumnData: {
        let columnDataNode = node as unknown as VariableResultColumnData

        const table = dbClient.tablesMap.get(columnDataNode.tbl!)
        const rowIdx = rowId ? table?.GetRowIndexByRowId(rowId, lang, user) : -1

        const columnDataValue = parseSingleCellValue(
          {
            tbl: columnDataNode.tbl,
            col: columnDataNode.col,
            userSpecific: columnDataNode.userSpecific,
            row: String(rowIdx),
          } as VariableResultSingleCell,
          dbClient,
          lang,
          user
        )
        if (!columnDataValue) break
        result += columnDataValue
        break
      }

      case VariableTextFeatureFlag.CurrentRow: {
        let currentRowNode = node as unknown as VariableResultColumnData

        const table = dbClient.tablesMap.get(tableId!)
        const rowIdx = rowId ? table?.GetRowIndexByRowId(rowId, lang, user) : -1

        const currentRowValue = parseSingleCellValue(
          {
            tbl: tableId,
            row: String(rowIdx),
            col: currentRowNode.col,
            userSpecific: currentRowNode.userSpecific,
          } as VariableResultSingleCell,
          dbClient,
          lang,
          user
        )
        if (!currentRowValue) break
        result += currentRowValue
        break
      }

      case VariableTextFeatureFlag.Column: {
        let columnNode = node as unknown as VariableResultColumn

        const table = dbClient.tablesMap.get(tableId!)
        const rowIdx = rowId ? table?.GetRowIndexByRowId(rowId, lang, user) : -1

        const columnValue = parseSingleCellValue(
          {
            tbl: tableId,
            row: String(rowIdx),
            col: columnNode.col,
          } as VariableResultSingleCell,
          dbClient,
          lang,
          user
        )
        if (!columnValue) break
        result += columnValue
        break
      }

      case VariableTextFeatureFlag.ProfileData: {
        const profileDataValue = parseProfileData(
          (node as unknown as VariableResultProfileData).col,
          dbClient,
          userEmail
        )
        if (!profileDataValue) break
        result += profileDataValue
        break
      }

      case VariableTextFeatureFlag.SessionID: {
        const sessionIDValue = cookies.getAppSessionId(window.appID)
        if (!sessionIDValue) break
        result += sessionIDValue
        break
      }

      case VariableTextFeatureFlag.UniqueID: {
        const uniqueIDValue = generateRandomId()
        if (!uniqueIDValue) break
        result += uniqueIDValue
        break
      }

      case VariableTextFeatureFlag.NewsletterEmail: {
        result += external?.newsletter?.email || ''
        break
      }

      case VariableTextFeatureFlag.DateTime: {
        const currentDateTime = new Date().toISOString()
        result += currentDateTime
        break
      }

      case VariableTextFeatureFlag.ActionOutput: {
        let actionOutputNode = node as any

        if (actionOutputNode.actionType === IActionType.TemporaryVariable) {
          const theValue = await parseTemporaryVariable(
            actionOutputNode.actionId ?? '',
            allActions ?? [],
            dbClient,
            container,
            userEmail,
            lang,
            user,
            tableId,
            rowId,
            outputCache
          )
          if (!theValue) break
          result += theValue
          break
        }

        if (actionOutputNode.actionType === IActionType.FilterData) {
          const actionOutputValue = await parseFilterDataAsync(
            actionOutputNode.value.actionId ?? '',
            allActions ?? [],
            dbClient,
            container,
            userEmail,
            lang,
            user,
            {
              detailsPage: {
                detailTableId: tableId,
                detailRowId: rowId,
              },
            }
          )
          const chosenColumn = actionOutputNode.value.colId
          if (!actionOutputValue) break

          if (Array.isArray(actionOutputValue)) {
            result += actionOutputValue.map((item) => item[chosenColumn ?? ''] ?? '').join('')
          } else if (typeof actionOutputValue === 'object') {
            result += actionOutputValue[chosenColumn ?? ''] ?? ''
          } else {
            result += actionOutputValue ?? ''
          }
        }

        if (actionOutputNode.actionType === IActionType.FormAddRow) {
          const data = outputCache?.get(actionOutputNode.value.actionId)
          if (data) {
            result += data[actionOutputNode.value.colId]?.toString() ?? ''
          }
        }
        break
      }

      case VariableTextFeatureFlag.GlobalVariable: {
        const varName = (node as unknown as VariableResultGlobalVariable).value
        const value = globalVariableClient?.getValue(varName)
        if (!value) break

        result += value
        break
      }

      default: {
        const n = node as unknown as VariableResultCommon
        if (n.value) {
          result += n.value
        }
        break
      }
    }

    result += children
  }

  return result
}
